import type { BroadcastContent, FreeTextInputMode, FreeTextProperties } from '../../../api/types';
import { Spinner } from '../../Spinner';
import { useSyncedTexts } from '../../../contexts/SyncedTextContext';
import { freeTextTemplateRouteLabel } from '../../../data/freeTextTemplates';
import type { GtfsStop } from '../../../data/gtfsStops';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';
import type { ValidationIssue } from '../../../utils/validationIssues';

import { BroadcastConditionsEditor } from '../conditions/BroadcastConditionsEditor';
import { HintTooltip } from '../../HintTooltip';
import { FREE_TEXT_MODE_META, FREE_TEXT_MODE_TESTID } from '../constants';
import { useAudioUpload } from '../hooks/useAudioUpload';
import { resolveInputMode } from '../logic';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import { LocalizedTextFields } from '../parts/LocalizedTextFields';
import { FreeTextTemplatePicker } from '../parts/FreeTextTemplatePicker';
import type { PreviewSsmlHandler } from '../types';

export function FreeTextFields({
  props,
  content,
  contentIndex = 0,
  routes,
  routeStops,
  gtfsStops,
  parentRouteId,
  parentVoiceId,
  playbackRate,
  issues = [],
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: {
  props: Partial<FreeTextProperties>;
  /**
   * プレビュー対象の放送コンテンツ (Issue #439)。
   * サーバ側でこの内容をレンダリングして実放送と同一の文面を合成する。
   * 音声ファイルモードでは Polly を呼ばず S3 の音源をそのまま再生する。
   */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 放送条件「停車番線」の番線ドロップダウン用の GTFS 静的 stops (全件)。
   * `route_id` で絞り込めないため親ページが 1 回だけ取得して渡す。
   * 詳細は `BroadcastContentsEditorProps.gtfsStops` を参照。
   */
  gtfsStops?: GtfsStop[];
  /**
   * 放送条件「停車番線」の停車駅ドロップダウンを自路線に絞り込むための route_id。
   * `routeStops` の先頭キーの暗黙採用は禁止。
   * 詳細は `BroadcastContentsEditorProps.parentRouteId` を参照。
   */
  parentRouteId?: string;
  /**

   * 親フォームで選択中の Polly VoiceId。プレビュー合成 (POST /audio-preview) に
   * `voiceId` として渡すことで、実放送と同じ声色で試聴できるようにする。
   * 空文字 / 未指定なら voiceId を送らず (= サーバー側デフォルト Takumi)。
   */
  parentVoiceId?: string;
  /**
   * 親コンテンツの `playback_rate` (要件 2)。
   * <audio> のプレビュー再生時に `playbackRate` として反映する。
   * `undefined` / `1` は等倍再生。範囲 (0.5〜2.0) 外の値は 1.0 に落として無視する。
   */
  playbackRate?: number;
  /**
   * このコンテンツに紐づく入力エラー (Issue #545)。
   *
   * 言語別文面の未入力を該当言語の入力欄へ赤枠で示すために
   * `LocalizedTextFields` へそのまま渡す。
   */
  issues?: ValidationIssue[];
  onChange: (props: FreeTextProperties) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}): React.JSX.Element {
  const mode = resolveInputMode(props);
  const plainText = props.text ?? '';
  const ssmlText = props.ssml ?? '';
  const languages = content?.languages ?? ['ja'];

  // 共有テキスト: 参照先の本文 (表示専用。放送地点には保存しない)。
  // 参照を決めているのにマスタに無い (削除済み) 場合は undefined となり、警告を出す。
  const syncedTexts = useSyncedTexts();
  const syncedRef = props.synced_ref;
  const syncedMaster = syncedRef
    ? syncedTexts.find(
        (t) => t.route_id === syncedRef.route_id && t.template_id === syncedRef.template_id,
      )
    : undefined;
  const syncedText = syncedMaster?.text;
  // 共有テキストは日本語専用: 放送言語が ['ja'] 以外のときは選べない。
  // `languages` 未設定は「日本語のみ」(既存データの後方互換)。
  const syncedAvailable = languages.every((language) => language === 'ja');

  // 音声ファイルアップロードの副作用 (アップロード中フラグ / エラー) は
  // `useAudioUpload` に切り出している。
  const { uploading, uploadError, handleFileChange } = useAudioUpload({
    onUploadAudio,
    onUploaded: (audioKey) => onChange({ ...props, input_mode: 'audio', audio_key: audioKey }),
  });

  /*
   * プレビュー可否 (Issue #439)。
   *
   * 従来は `mode !== 'audio'` を明確にプレビュー対象外にしていたが、現場的には
   * 「アップロードした MP3 が正しいか」「音量・尺が適切か」の確認需要が最も強い。
   * サーバ側 (`preview_render.py`) が音声ファイルモードでは Polly を呼ばず
   * S3 の音源をそのまま返すため、**追加コストゼロ** で試聴できるようになった。
   */
  const previewableText = mode === 'ssml' ? ssmlText : mode === 'text' ? plainText : '';
  const canPreview =
    mode === 'audio'
      ? (props.audio_key ?? '').trim() !== ''
      : mode === 'synced'
        ? syncedText !== undefined
        : mode === 'text'
          ? languages.some(
              (language) =>
                (language === 'ja'
                  ? plainText
                  : props.text_i18n?.[language] || plainText
                ).trim() !== '',
            )
          : previewableText.trim() !== '';
  const previewDisabledReason =
    mode === 'audio'
      ? '音声ファイルをアップロードすると試聴できます'
      : mode === 'synced'
        ? '配布テンプレートを選択すると試聴できます'
        : '文面を入力すると試聴できます';

  const handleModeChange = (next: FreeTextInputMode): void => {
    // 共有テキストは参照のみを保存する。他モードへ戻すときは参照を捨て、
    // 古い参照が保存され続けるのを防ぐ (サーバも synced 以外では参照を検査しない)。
    if (next !== 'synced') {
      const { synced_ref: _dropped, ...rest } = props;
      void _dropped;
      onChange({ ...rest, input_mode: next });
      return;
    }
    onChange({ ...props, input_mode: next });
  };

  /** 「登録済みテキストから選択」(任意テキスト + 共有テキスト) */
  const renderTemplatePicker = (): React.JSX.Element => (
    <FreeTextTemplatePicker
      currentText={mode === 'synced' ? '' : plainText}
      routeId={parentRouteId}
      routes={routes}
      syncedRef={syncedRef}
      syncedAvailable={syncedAvailable}
      onApply={(text) => {
        // 共有テキストを参照中に任意テキストを選んだときは参照を捨てる。
        const { synced_ref: _dropped, ...rest } = props;
        void _dropped;
        onChange({ ...rest, input_mode: 'text', text });
      }}
      onApplySynced={(ref) => {
        // 本文系の値 (text / ssml / audio_key とその言語別) は保存しない (サーバも保存時に除く)。
        const {
          text: _text,
          text_i18n: _textI18n,
          ssml: _ssml,
          ssml_i18n: _ssmlI18n,
          audio_key: _audioKey,
          audio_key_i18n: _audioKeyI18n,
          ...rest
        } = props;
        void [_text, _textI18n, _ssml, _ssmlI18n, _audioKey, _audioKeyI18n];
        onChange({ ...rest, input_mode: 'synced', synced_ref: ref });
      }}
    />
  );

  const handleRemoveAudio = (): void => {
    const next = { ...props };
    delete next.audio_key;
    onChange(next);
  };

  return (
    <div className="contents-fields">
      {/*
        放送条件セクション (任意テキスト放送の先頭に配置)。
        任意テキスト放送はデフォルトでは無条件で放送されるが、ここで放送条件 (接続線遅延) を
        複数追加することで「〜のとき限定放送」に絞り込める (AND 判定)。
        UI・データモデルは接続線遅延コンテンツ (ConnectingLineDelayFields) を踏襲する。

        コンポーネント実装は別ファイル `./BroadcastConditionsEditor.tsx` を参照。
      */}
      <BroadcastConditionsEditor
        conditions={Array.isArray(props.conditions) ? props.conditions : []}
        routes={routes}
        routeStops={routeStops}
        gtfsStops={gtfsStops}
        parentRouteId={parentRouteId}
        onChange={(next) => {
          const nextProps: FreeTextProperties = { ...props };
          if (next.length === 0) {
            delete nextProps.conditions;
          } else {
            nextProps.conditions = next;
          }
          onChange(nextProps);
        }}
      />

      <fieldset className="free-text-mode-switch" style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ padding: 0 }}>入力方法</legend>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {FREE_TEXT_MODE_META.map((m) => (
            <label key={m.mode} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="radio"
                /*
                 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」の
                 * ラジオボタンを安定して特定できるよう data-testid を付与する。
                 *
                 * 画面ラベルは日本語 ("通常テキスト" など) だが value は
                 * FreeTextInputMode キー ("text" / "ssml" / "audio") であり、
                 * "plain" のような別名ではない。AI が画面ラベルから推測した
                 * セレクタ (例: input[value='plain']) を投入して 3 連続失敗で
                 * ステップが中止される事故が報告されたため、testid を付ける。
                 */
                data-testid={FREE_TEXT_MODE_TESTID[m.mode]}
                aria-label={`入力方法: ${m.label}`}
                value={m.mode}
                // 共有テキスト (synced) を参照中は「通常テキスト」を選択状態で示す。
                checked={mode === m.mode || (m.mode === 'text' && mode === 'synced')}
                onChange={() => handleModeChange(m.mode)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </fieldset>
      {mode === 'text' && (
        <LocalizedTextFields
          label="任意テキスト"
          languages={languages}
          value={props.text_i18n}
          // Issue #492 (SBI-11): 「日本語から翻訳」ボタンの翻訳元。
          // 通常テキストモードの日本語入力欄 (`props.text`) をそのまま渡す。
          sourceText={plainText}
          translateTestId={`free-text-${contentIndex}`}
          // Issue #545: 未入力の言語欄に赤枠を付けるためエラーを渡す。
          issues={issues}
          onChange={(value) =>
            onChange({
              ...props,
              text_i18n: value,
            })
          }
        />
      )}

      {/*
        Issue #545: SSML モードにも言語別入力欄を出す。

        従来は SSML モードに `ssml_i18n` の入力欄が無く、**選択した言語の文面を
        入力する手段が存在しなかった**。その結果「英語を選んだのに英語の放送枠で
        日本語 SSML が流れる」状態を運用者が直せなかった。
        入力欄を出したうえでバリデーション (未入力を保存前に弾く) を効かせる。
      */}
      {mode === 'ssml' && (
        <LocalizedTextFields
          label="任意テキスト SSML"
          languages={languages}
          value={props.ssml_i18n}
          // 翻訳元は SSML モードの日本語入力欄 (`props.ssml`)。
          sourceText={ssmlText}
          translateTestId={`free-text-${contentIndex}`}
          issues={issues}
          onChange={(value) =>
            onChange({
              ...props,
              ssml_i18n: value,
            })
          }
        />
      )}

      {mode === 'text' && languages.includes('ja') ? (
        <>
          {/*
            登録済みテキスト (独自テンプレート / 配布テンプレート) から選ぶ。
            任意テキストは文面をコピーし、入力済みの文面があるときは上書きの確認ダイアログを経る。
            配布テンプレートは参照だけを保存する (下の `mode === 'synced'` の表示になる)。
            候補が無い (未登録 / 取得失敗) ときは何も描画しない。
          */}
          {renderTemplatePicker()}
          <label>
            {/*
             * 画面表示ラベル (<span>) と aria-label は完全一致させること。
             * 不一致だと E2E / AI エージェントが画面ラベルから要素を特定できず、
             * モーダル内の他のテキスト入力欄 (説明欄: data-testid="description-input") に
             * 誤入力してしまう事象が発生する。
             * 併せて data-testid="content-free-text-plain" でも一意に取得できるようにする。
             */}
            <span>放送文面 (通常テキスト)</span>
            <textarea
              rows={4}
              aria-label="放送文面 (通常テキスト)"
              data-testid="content-free-text-plain"
              value={plainText}
              onChange={(e) =>
                onChange({
                  ...props,
                  input_mode: 'text',
                  text: e.target.value,
                })
              }
              placeholder="まもなく終点です"
            />
          </label>
        </>
      ) : null}

      {mode === 'ssml' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致 ("放送文面 SSML")。
           * 併せて data-testid="content-free-text-ssml" でも一意に取得できるようにする。
           */}
          <span>放送文面 SSML</span>
          <textarea
            rows={4}
            aria-label="放送文面 SSML"
            data-testid="content-free-text-ssml"
            value={ssmlText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'ssml',
                ssml: e.target.value,
              })
            }
            placeholder="<speak>まもなく終点です</speak>"
          />
        </label>
      ) : null}

      {mode === 'synced' ? (
        <>
          {/*
            配布テンプレート: 本文はコピーせずマスタを参照する。下のテキストエリアは表示専用 (編集不可)。
            本文の編集は「任意テキスト」画面の「配布テンプレート」で行い、約 1 分以内に放送へ反映される。
            別のテキストへ変える / 通常テキストへ戻すのはどちらも「登録済みテキストから選択」から。
          */}
          {renderTemplatePicker()}
          <label>
            <span>放送文面 (配布テンプレート)</span>
            <textarea
              rows={4}
              disabled
              aria-label="放送文面 (配布テンプレート)"
              data-testid="content-synced-text-body"
              value={syncedText ?? ''}
              placeholder="配布テンプレートを選択するとここに本文が表示されます"
              readOnly
            />
            {syncedRef && syncedMaster ? (
              <small data-testid="content-synced-text-source">
                参照先: {freeTextTemplateRouteLabel(syncedRef.route_id, routes)}
              </small>
            ) : null}
          </label>
          {/*
            参照切れ: 参照先がマスタに無い (削除済み / 一覧の取得失敗)。
            このままだとこのコンテンツは放送されないので、赤字で知らせる。
          */}
          {syncedRef && !syncedMaster ? (
            <div className="error-message" role="alert" data-testid="content-synced-text-missing">
              参照先の配布テンプレートが見つかりません（削除された可能性があります）。このままでは放送されません。もう一度選択してください。
            </div>
          ) : null}
          {!syncedAvailable ? (
            <div
              className="error-message"
              role="alert"
              data-testid="content-synced-text-language-warning"
            >
              配布テンプレートは日本語専用です。放送言語を「日本語のみ」にするか、入力方法を変更してください。
            </div>
          ) : null}
        </>
      ) : null}

      {/*
        音声プレビュー (Issue #439)。
        共通部品 `AudioPreviewField` に置き換え、**音声ファイルモードでも** 試聴できる
        ようにした (サーバ側が Polly を呼ばず S3 の音源をそのまま返すため追加コストゼロ)。
        既存 E2E 互換のため <audio> の data-testid は `ssml-preview-audio` を維持する。
      */}
      {content ? (
        <AudioPreviewField
          content={content}
          parentVoiceId={parentVoiceId}
          playbackRate={playbackRate}
          onPreviewSsml={onPreviewSsml}
          testIdSuffix={`free-text-${contentIndex}`}
          legacyAudioTestId="ssml-preview-audio"
          disabled={!canPreview}
          disabledReason={previewDisabledReason}
        />
      ) : null}

      {mode === 'audio' ? (
        <div className="audio-upload-field">
          <span>放送音声ファイル (MP3)</span>
          {props.audio_key ? (
            <div className="audio-upload-status" data-testid="audio-uploaded">
              <span>アップロード済み: {props.audio_key}</span>
              {/*
                Issue #439: 「アップロード済み: xxx.mp3」の隣に試聴ボタンを置く。
                上の共通プレビュー (AudioPreviewField) と同じ経路で鳴るが、
                音源の確認は「アップロード済み」表示のすぐ横にある方が迷わないため、
                チャイム画面 (ChimeMasterScreen) の「▶ 試聴」と同じ位置関係にする。
              */}
              <span className="hint" data-testid="audio-uploaded-preview-hint">
                上の「▶ 音声プレビュー」でこの音源を試聴できます
              </span>
              <button type="button" className="danger" onClick={handleRemoveAudio}>
                音声を削除
              </button>
            </div>
          ) : (
            <p style={{ margin: '4px 0' }}>
              <span style={{ fontSize: 12 }}>MP3 音声ファイルをアップロード</span>
              <HintTooltip label="音声ファイルの説明" testId="audio-upload-hint">
                MP3
                音声ファイルをアップロードしてください。アップロードした音声がそのまま放送されます。
              </HintTooltip>
            </p>
          )}
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            aria-label="放送音声ファイル (MP3)"
            disabled={uploading || !onUploadAudio}
            onChange={(e) => void handleFileChange(e)}
          />
          {uploading ? (
            <span className="hint">
              <Spinner />
              アップロード中...
            </span>
          ) : null}
          {uploadError ? (
            <div className="error-message" role="alert">
              {uploadError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
